import React, { useState, useEffect } from 'react';
import { Lightbulb, AlertTriangle, Rocket, User, Building2, CheckCircle, ArrowRight, ChevronDown } from 'lucide-react';
import axios from 'axios';

/**
 * 风险与机会头脑风暴 - 独立填写页面（无需登录）
 * 访问方式：扫描二维码 → ?standalone=brainstorm
 */
export default function BrainstormStandalone() {
  const [step, setStep] = useState(0); // 0: 填写信息, 1: AI生成, 2: 分类, 3: 完成
  const [userName, setUserName] = useState('');
  const [orgStructure, setOrgStructure] = useState(null);
  const [selectedLevel1, setSelectedLevel1] = useState('');
  const [selectedLevel2, setSelectedLevel2] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [items, setItems] = useState([]);
  const [risks, setRisks] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(false);

  // 加载组织架构
  useEffect(() => {
    fetch('/org-structure.json')
      .then(res => res.json())
      .then(data => setOrgStructure(data))
      .catch(err => console.error('加载组织架构失败:', err));
  }, []);

  // 获取AI配置（从localStorage读取）
  const getAIConfig = () => {
    try {
      const aiConfigs = JSON.parse(localStorage.getItem('ai_api_configs') || '[]');
      const minimaxModel = aiConfigs.find(m => {
        const provider = m.provider || m.providerId;
        return provider === 'minimax';
      });
      
      if (minimaxModel) {
        console.log('🤖 使用前端AI配置:', minimaxModel.name);
        return {
          apiKey: minimaxModel.apiKey,
          model: minimaxModel.model || 'MiniMax-M2.7',
          baseUrl: minimaxModel.endpoint || minimaxModel.baseUrl || 'https://api.minimaxi.com/v1',
        };
      }
    } catch (error) {
      console.warn('读取AI配置失败:', error);
    }
    return null;
  };

  // 获取一级部门列表
  const level1Departments = orgStructure?.children || [];

  // 获取二级部门列表
  const level2Departments = level1Departments.find(d => d.id === selectedLevel1)?.children || [];

  // 获取完整的部门路径（用于显示）
  const getDepartmentPath = () => {
    if (!selectedLevel1 || !selectedLevel2) return '';
    const level1Name = level1Departments.find(d => d.id === selectedLevel1)?.name || '';
    const level2Name = level2Departments.find(d => d.id === selectedLevel2)?.name || '';
    return `${level1Name}-${level2Name}`;
  };

  // 提交基本信息并生成
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!userName.trim()) {
      alert('请输入您的姓名');
      return;
    }
    
    if (!selectedLevel1) {
      alert('请选择一级部门');
      return;
    }
    
    if (!selectedLevel2) {
      alert('请选择二级部门');
      return;
    }

    const level1Name = level1Departments.find(d => d.id === selectedLevel1)?.name || '';
    const level2Name = level2Departments.find(d => d.id === selectedLevel2)?.name || '';
    const module = `${level1Name}-${level2Name}`;

    setLoading(true);
    try {
      // 获取AI配置
      const aiConfig = getAIConfig();
      
      const response = await axios.post('/api/brainstorm/generate', {
        module: level2Name, // 使用二级部门名称作为模块
        groupCount: 1,
        itemsPerGroup: 15,
        userId: `standalone-${Date.now()}`,
        userName,
        level1Department: level1Name,
        level2Department: level2Name,
        aiConfig // 传递AI配置
      });
      
      if (response.data.success) {
        const allItems = response.data.groups.flatMap(g => g.items);
        setSessionId(response.data.sessionId);
        setItems(allItems);
        setStep(2); // 跳过步骤1，直接到分类
      }
    } catch (error) {
      console.error('生成失败:', error);
      alert('生成失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 分类为风险
  const classifyAsRisk = (item) => {
    setItems(items.filter(i => i.id !== item.id));
    setRisks([...risks, item]);
  };

  // 分类为机会
  const classifyAsOpportunity = (item) => {
    setItems(items.filter(i => i.id !== item.id));
    setOpportunities([...opportunities, item]);
  };

  // 撤销分类
  const unclassify = (item, type) => {
    if (type === 'risk') {
      setRisks(risks.filter(i => i.id !== item.id));
    } else {
      setOpportunities(opportunities.filter(i => i.id !== item.id));
    }
    setItems([...items, item]);
  };

  // 完成分类
  const handleComplete = async () => {
    if (items.length > 0) {
      alert('请先完成所有项目的分类');
      return;
    }

    try {
      await axios.post('/api/brainstorm/classify', {
        sessionId,
        classification: { risks, opportunities },
        userName,
        module
      });
      setStep(3);
    } catch (error) {
      console.error('保存失败:', error);
      alert('保存失败，但您的分类已完成');
      setStep(3);
    }
  };

  return (
    <div className="min-h-screen max-h-screen overflow-y-auto bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* 顶部标题 */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-base font-bold text-gray-800">风险与机会头脑风暴</h1>
                <p className="text-xs text-gray-500">AI驱动的风险识别工具</p>
              </div>
            </div>
            {userName && (
              <div className="flex items-center space-x-1 text-xs text-gray-600">
                <User className="w-3 h-3" />
                <span className="max-w-[60px] truncate">{userName}</span>
                {getDepartmentPath() && (
                  <>
                    <span className="text-gray-400">|</span>
                    <span className="max-w-[80px] truncate text-[10px]">{getDepartmentPath()}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 pb-20">
        {/* 步骤0: 填写基本信息 */}
        {step === 0 && (
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
              <div className="text-center mb-6 sm:mb-8">
                <div className="w-14 sm:w-16 h-14 sm:h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <Lightbulb className="w-7 sm:w-8 h-7 sm:h-8 text-white" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">欢迎参与</h2>
                <p className="text-gray-600 text-sm sm:text-base">请填写您的基本信息开始头脑风暴</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                {/* 姓名 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-1" />
                    您的姓名 *
                  </label>
                  <input
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="请输入您的姓名"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base"
                    required
                  />
                </div>

                {/* 一级部门 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building2 className="w-4 h-4 inline mr-1" />
                    一级部门 *
                  </label>
                  <div className="relative">
                    <select
                      value={selectedLevel1}
                      onChange={(e) => {
                        setSelectedLevel1(e.target.value);
                        setSelectedLevel2(''); // 重置二级部门
                      }}
                      className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none bg-white text-base"
                      required
                    >
                      <option value="">请选择一级部门</option>
                      {level1Departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* 二级部门 */}
                {selectedLevel1 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Building2 className="w-4 h-4 inline mr-1" />
                      二级部门 *
                    </label>
                    <div className="relative">
                      <select
                        value={selectedLevel2}
                        onChange={(e) => setSelectedLevel2(e.target.value)}
                        className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none bg-white text-base"
                        required
                      >
                        <option value="">请选择二级部门</option>
                        {level2Departments.map(dept => (
                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                )}

                {/* 提交按钮 */}
                <button
                  type="submit"
                  disabled={loading || !userName.trim() || !selectedLevel1 || !selectedLevel2}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 sm:py-4 px-6 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 text-base"
                >
                  {loading ? (
                    <span>AI生成中...</span>
                  ) : (
                    <>
                      <span>开始头脑风暴</span>
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-5 sm:mt-6 pt-5 sm:pt-6 border-t border-gray-200">
                <p className="text-xs sm:text-sm text-gray-500 text-center">
                  本工具由AI驱动，将根据您的部门自动生成相关的风险与机会项目
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 步骤2: 分类 */}
        {step === 2 && (
          <div>
            <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-800">
                  请将以下项目分类为风险或机会
                </h2>
                <div className="text-sm text-gray-600">
                  剩余 <span className="font-bold text-blue-600">{items.length}</span> 项
                </div>
              </div>
              <p className="text-gray-600 text-sm sm:text-base">
                根据您的判断，点击"风险"或"机会"按钮进行分类
              </p>
            </div>

            {/* 移动端：单列布局；PC端：三列布局 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {/* 待分类 */}
              <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
                <h3 className="font-bold text-base sm:text-lg mb-3 sm:mb-4 flex items-center text-gray-700">
                  <span className="w-3 h-3 bg-gray-400 rounded-full mr-2"></span>
                  待分类 ({items.length})
                </h3>
                <div className="space-y-3 max-h-[50vh] sm:max-h-[600px] overflow-y-auto">
                  {items.map(item => (
                    <div key={item.id} className="bg-gray-50 p-3 sm:p-4 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-all">
                      <p className="text-sm text-gray-700 mb-3 leading-relaxed">{item.text}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => classifyAsRisk(item)}
                          className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white px-2 sm:px-3 py-2 rounded-lg text-sm font-medium hover:from-red-600 hover:to-red-700 transition-all shadow-sm hover:shadow flex items-center justify-center space-x-1"
                        >
                          <AlertTriangle className="w-4 h-4" />
                          <span>风险</span>
                        </button>
                        <button
                          onClick={() => classifyAsOpportunity(item)}
                          className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-2 sm:px-3 py-2 rounded-lg text-sm font-medium hover:from-blue-600 hover:to-blue-700 transition-all shadow-sm hover:shadow flex items-center justify-center space-x-1"
                        >
                          <Rocket className="w-4 h-4" />
                          <span>机会</span>
                        </button>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && (
                    <div className="text-center py-8 sm:py-12 text-gray-400">
                      <CheckCircle className="w-10 sm:w-12 mx-auto mb-2" />
                      <p>已全部分类完成</p>
                    </div>
                  )}
                </div>
              </div>

              {/* 风险 */}
              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl shadow-xl p-4 sm:p-6">
                <h3 className="font-bold text-base sm:text-lg mb-3 sm:mb-4 flex items-center text-red-700">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  风险 ({risks.length})
                </h3>
                <div className="space-y-3 max-h-[50vh] sm:max-h-[600px] overflow-y-auto">
                  {risks.map(item => (
                    <div key={item.id} className="bg-white p-3 sm:p-4 rounded-lg border-2 border-red-200 shadow-sm">
                      <p className="text-sm text-gray-700 mb-2 leading-relaxed">{item.text}</p>
                      <button
                        onClick={() => unclassify(item, 'risk')}
                        className="text-xs text-red-600 hover:text-red-800 font-medium"
                      >
                        撤销
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* 机会 */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl shadow-xl p-4 sm:p-6">
                <h3 className="font-bold text-base sm:text-lg mb-3 sm:mb-4 flex items-center text-blue-700">
                  <Rocket className="w-5 h-5 mr-2" />
                  机会 ({opportunities.length})
                </h3>
                <div className="space-y-3 max-h-[50vh] sm:max-h-[600px] overflow-y-auto">
                  {opportunities.map(item => (
                    <div key={item.id} className="bg-white p-3 sm:p-4 rounded-lg border-2 border-blue-200 shadow-sm">
                      <p className="text-sm text-gray-700 mb-2 leading-relaxed">{item.text}</p>
                      <button
                        onClick={() => unclassify(item, 'opportunity')}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        撤销
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4 sm:mt-6 text-center">
              <button
                onClick={handleComplete}
                disabled={items.length > 0}
                className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-medium hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl inline-flex items-center space-x-2 text-sm sm:text-base"
              >
                <CheckCircle className="w-5 h-5" />
                <span>完成分类</span>
              </button>
            </div>
          </div>
        )}

        {/* 步骤3: 完成 */}
        {step === 3 && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-12 text-center">
              <div className="w-16 sm:w-20 h-16 sm:h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <CheckCircle className="w-10 sm:w-12 h-10 sm:h-12 text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3 sm:mb-4">提交成功！</h2>
              <p className="text-gray-600 mb-6 sm:mb-8">
                感谢 <span className="font-bold text-blue-600">{userName}</span> 的参与！
              </p>

              <div className="grid grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="bg-red-50 rounded-xl p-4 sm:p-6 border-2 border-red-200">
                  <div className="text-3xl sm:text-4xl font-bold text-red-600 mb-2">{risks.length}</div>
                  <div className="text-sm text-gray-600">识别的风险</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 sm:p-6 border-2 border-blue-200">
                  <div className="text-3xl sm:text-4xl font-bold text-blue-600 mb-2">{opportunities.length}</div>
                  <div className="text-sm text-gray-600">识别的机会</div>
                </div>
              </div>

              <p className="text-sm sm:text-base text-gray-500">
                您的分类结果已保存，管理员将进行后续分析处理
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
